import { useEffect, useState, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SuggestionApproveInput, SuggestionFilters } from '../api/types';
import { CheckboxField, Note, Segmented } from '../workspace/ui';
import type { SuggestedSearch } from './model';
import { SoundLink } from '../components/SoundLink';

const CADENCES = [
  { value: '1', label: 'Daily' },
  { value: '3', label: 'Every 3 days' },
  { value: '7', label: 'Weekly' },
  { value: '14', label: 'Every 2 weeks' },
] as const;
type Cadence = (typeof CADENCES)[number]['value'];

/** The filters a search will run with, in a few words — for the bulk confirm. */
export function filtersInWords(f: SuggestionFilters): string {
  const parts: string[] = [];
  if (f.minFollowers != null) parts.push(`at least ${f.minFollowers.toLocaleString()} followers`);
  if (f.maxFollowers != null) parts.push(`at most ${f.maxFollowers.toLocaleString()} followers`);
  if (f.minAvgViews != null) parts.push(`${f.minAvgViews.toLocaleString()}+ average views`);
  if (f.maxLastPostedDays != null) parts.push(`posted in the last ${f.maxLastPostedDays} days`);
  if (f.minTopicMatch != null) parts.push(`topic match ${f.minTopicMatch} or better`);
  if (f.targetCount != null) parts.push(`${f.targetCount.toLocaleString()} creators each`);
  return parts.join(', ');
}

/**
 * Filter review before a suggested term may spend scrape budget. Prefilled
 * from the server's own defaults (fetched, so the UI can't drift from what's
 * applied). Blank means "no limit".
 *
 * "Approve & schedule" opens the same review with repeating already on:
 * repeating stays a choice made next to the filters it will keep re-running.
 */
export function ApproveDialog({
  suggestion,
  schedule,
  defaults,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  suggestion: SuggestedSearch | null;
  /** Open with "repeat" already ticked. */
  schedule: boolean;
  defaults: SuggestionFilters;
  busy: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: (input: SuggestionApproveInput) => void;
}): JSX.Element {
  const [f, setF] = useState<SuggestionFilters>(defaults);
  const [terms, setTerms] = useState('');
  const [repeat, setRepeat] = useState(false);
  const [cadence, setCadence] = useState<Cadence>('7');
  const id = suggestion?.publicId ?? null;
  useEffect(() => {
    if (!suggestion) return;
    setF(defaults);
    setTerms((suggestion.topicTerms ?? []).join(', '));
    setRepeat(schedule);
    setCadence('7');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, schedule]);

  const termList = terms
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  // A sound with no terms can't be topic-filtered; the server refuses it.
  const soundNeedsTerms = suggestion?.searchMode === 'sound' && f.minTopicMatch != null && termList.length === 0;

  const num = (key: keyof SuggestionFilters, label: string, hint?: string) => (
    <div className="grid gap-1">
      <Label htmlFor={`approve-${key}`}>{label}</Label>
      <Input
        id={`approve-${key}`}
        type="number"
        value={(f[key] as number | undefined) ?? ''}
        placeholder="No limit"
        onChange={(e) => setF((prev) => ({ ...prev, [key]: e.target.value === '' ? undefined : Number(e.target.value) }))}
      />
      {hint && <span className="text-sm text-muted-foreground">{hint}</span>}
    </div>
  );

  return (
    <Dialog open={!!suggestion} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-xl">
        {suggestion && (
          <>
            <DialogHeader>
              <DialogTitle>
                Run {suggestion.searchMode === 'sound' ? `the sound "${suggestion.term}"` : `#${suggestion.term}`}
              </DialogTitle>
              <DialogDescription>
                Results land in a new folder. These thresholds decide what's kept — an unfiltered run on an unproven term
                mostly returns creators you'd delete.
              </DialogDescription>
            </DialogHeader>

            {suggestion.searchMode === 'sound' && suggestion.musicId && (
              // Listen before spending a search on it.
              <div className="flex items-center rounded-lg border border-border bg-muted/40 p-1.5">
                <SoundLink variant="row" platform={suggestion.platform} id={suggestion.musicId} title={suggestion.term} />
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {num('minFollowers', 'Min followers', 'Below ~5k they rarely clear campaign minimums')}
              {num('maxFollowers', 'Max followers')}
              {num('minAvgViews', 'Min avg views')}
              {num('maxLastPostedDays', 'Posted within (days)', 'Dormant accounts won’t start clipping')}
              {num('minTopicMatch', 'Min topic match (0–1)', 'Best defence against an off-topic term')}
              {num('targetCount', 'How many to find')}
            </div>

            <div className="grid gap-1">
              <Label htmlFor="approve-terms">On-topic terms</Label>
              <Input id="approve-terms" value={terms} placeholder="anime, edit" onChange={(e) => setTerms(e.target.value)} />
              <span className="text-sm text-muted-foreground">
                {suggestion.searchMode === 'sound'
                  ? 'A sound returns everyone using it. These terms keep only the creators actually making this kind of content.'
                  : 'Checked against each creator’s recent posts, so someone who used the tag once but posts something else is dropped.'}
              </span>
            </div>

            {soundNeedsTerms && (
              <Note tone="warning">
                Add at least one term, or clear the minimum topic match. Without terms the topic filter can’t be applied and
                every creator using this sound would be kept.
              </Note>
            )}

            <div className="grid gap-2">
              <CheckboxField checked={f.excludeOfficial ?? false} onChange={(v) => setF((p) => ({ ...p, excludeOfficial: v }))}>
                Skip brand and official accounts
              </CheckboxField>
              <CheckboxField
                checked={repeat}
                onChange={setRepeat}
                hint={repeat ? 'It runs once now, then again on this cadence. Watch it on Repeat runs.' : undefined}
              >
                Repeat this search with these same filters
              </CheckboxField>
              {repeat && (
                <Segmented label="How often" value={cadence} onChange={setCadence} options={CADENCES} className="ml-[26px] w-fit" />
              )}
            </div>
            {error && <Note tone="error">{error.message}</Note>}

            <DialogFooter>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                disabled={busy || soundNeedsTerms}
                onClick={() =>
                  onConfirm({
                    filters: f,
                    topicTerms: termList,
                    ...(repeat ? { isRecurring: true, recurEveryDays: Number(cadence) } : {}),
                  })
                }
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? 'Starting…' : repeat ? 'Run and repeat' : 'Run search'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
